import React, {useCallback, useState} from "react";
import {useViewController} from "data/services/locator/locator_hook.service";
import {ISecondRegistrationFormController} from "views/components/forms/second_registration_form/second_registration_form.controller";
import styled from "@emotion/styled";
import {FormControl, Stack, Typography} from "@mui/material";
import {ButtonPrimary} from "views/components/buttons/buttons.component";
import {observer} from "mobx-react";
import {Bindings} from "data/constants/bindings";
import {Checkbox} from "views/components/forms/custom_fields/checkbox.component";
import {eq, noop} from "lodash";
import {ICustomFieldValue} from "data/types/global";
import {NavLink} from "react-router-dom";
import {TEAM_DATA, TEAM_PREFIX} from "data/constants";
import {Exist} from "views/components/exist/exist.component";
import {TeamType} from "data/enums";

const Title = styled(Typography)`
	color: var(--textColorMain);
	letter-spacing: 0.2px;
	text-transform: uppercase;
`;

const SubmitButton = styled(ButtonPrimary)`
	height: 52px;
	width: 100%;
`;

const FlexContainer = styled.div`
	display: flex;
	flex-direction: row;
	align-items: center;
	gap: 0px;
	font-size: 14px;
	text-align: left;
	color: var(--textColorMain);

	span {
		margin: 0;
		color: var(--textColorMain);

		b {
			font-weight: bold;
		}
	}

	a {
		text-decoration: none;
		font-weight: bolder;
		color: var(--textColorMain);
		cursor: pointer;
	}
`;
const Description = styled(Typography)`
	color: var(--textColorMain);
	text-align: center;
	line-height: 125%; /* 20px */
	letter-spacing: 0.32px;
`;

export const SecondRegistrationFormComponent: React.FC = observer(() => {
	const fields = TEAM_DATA.registrationFields;

	const [requiredFields, setRequiredFields] = useState([
		{
			name: "terms",
			value: fields.terms ? "0" : "1",
		},
	]);

	const controller = useViewController<ISecondRegistrationFormController>(
		Bindings.SecondRegistrationFormController
	);
	const {
		i18n,
		handleFormChange,
		submit,
		isDisabledSubmit,
		updateFields,
		error,
		isNeedToShowOptInField,
	} = controller;

	const submitHandler = useCallback(
		(e: React.SyntheticEvent<HTMLFormElement, Event>) => {
			void submit(e);
		},
		[submit]
	);

	const checkBoxChange = useCallback(
		({value, name}: ICustomFieldValue) => {
			if (name === "terms") setRequiredFields([{name, value}]);
		},
		[setRequiredFields]
	);

	return (
		<React.Fragment>
			{/*<Title>{i18n.t("terms.request.title", "Terms and Conditions")}</Title>*/}
			<Title variant={"h2"}>
				{i18n.t(`terms.request.welcome_title_part_1`, `Welcome to`)}
			</Title>
			<Title variant={"h2"}>
				{i18n.t(`terms.request.welcome_title_${TEAM_PREFIX}`, `${TEAM_PREFIX} blitz picks`)}
			</Title>
			<Description variant={"body1"} fontWeight={"regular"}>
				{i18n.t(
					"terms.request.description",
					`To get started, accept our game terms to be eligible to win {{X}}`,
					{X: "Prizes"}
				)}
			</Description>

			<form onChange={handleFormChange} onSubmit={submitHandler}>
				<FormControl fullWidth sx={{mb: 3}}>
					<Stack gap={0.5} mt={2}>
						<Exist when={fields.terms}>
							<FlexContainer>
								<Checkbox
									onChange={checkBoxChange}
									defaultValue={updateFields.terms}
									disabled={false}
									isRequired={true}
									code={"terms"}
									id={1}
									fieldDescription={""}
								/>
								<span>
									{i18n.t(
										"terms.request.terms_label_p1",
										"I have read and agree to be bound by the contest"
									)}{" "}
									<NavLink rel={"noreferrer"} target={"_blank"} to={"/help/t_cs"}>
										{i18n.t(
											"terms.request.terms_label_p2",
											"Rules and Regulations"
										)}
									</NavLink>
								</span>
							</FlexContainer>
						</Exist>

						{isNeedToShowOptInField && (
							<FlexContainer>
								<Checkbox
									onChange={noop}
									defaultValue={updateFields.optInGZ}
									disabled={false}
									isRequired={false}
									code={"optInGZ"}
									id={2}
									error={error}
									fieldDescription={""}
								/>
								<span>
									{i18n.t(
										"terms.request.notifications_label",
										"Yes, I would like to receive breaking news, League updates and exclusive offers from the Canadian Football League (CFL) and/or its partners. You can withdraw your consent at any time by unsubscribing."
									)}
								</span>
							</FlexContainer>
						)}

						<Exist when={fields.marketing}>
							<FlexContainer>
								<Checkbox
									onChange={checkBoxChange}
									defaultValue={updateFields.teamMarketing}
									disabled={false}
									isRequired={false}
									code={"teamMarketing"}
									id={3}
									fieldDescription={""}
								/>
								<span>
									{i18n.t(
										"terms.request.team_marketing_label_p1",
										"I wish to receive"
									)}{" "}
									<b>
										{i18n.t(
											"terms.request.team_marketing_label_p2",
											"Team Marketing"
										)}
									</b>{" "}
									{i18n.t("terms.request.team_marketing_label_p3", "emails")}
								</span>
							</FlexContainer>
						</Exist>

						<Exist when={fields.sponsor}>
							<FlexContainer>
								<Checkbox
									onChange={checkBoxChange}
									defaultValue={updateFields.sponsorMarketing}
									disabled={false}
									isRequired={false}
									code={"sponsorMarketing"}
									id={4}
									fieldDescription={""}
								/>
								<Exist when={eq(TEAM_PREFIX, TeamType.TICATS)}>
									<span>
										{i18n.t(
											"terms.request.sponsor_marketing_ticats_label_p1",
											"I wish to receive emails from"
										)}{" "}
										<a
											href={TEAM_DATA.sponsorData?.sponsorLink}
											target="_blank"
											rel="noreferrer">
											{i18n.t(
												"terms.request.sponsor_marketing_ticats_label_p2",
												"Airline Tickets | Vacation Packages | Cruises & Last Minute Deals | Red Tag Vacations"
											)}
										</a>{" "}
										{i18n.t(
											"terms.request.sponsor_marketing_ticats__label_p3",
											" Some terms and conditions apply. Click"
										)}{" "}
										<NavLink to="/help/t_cs">
											{i18n.t(
												"terms.request.sponsor_marketing_ticats__label_here",
												"here"
											)}
										</NavLink>{" "}
										{i18n.t(
											"terms.request.sponsor_marketing_ticats__label_p4",
											"for additional details. You may unsubscribe at any time. See our"
										)}{" "}
										<a
											href="https://www.redtag.ca/privacy.php"
											target="_blank"
											rel="noreferrer">
											{i18n.t(
												"terms.request.sponsor_marketing_ticats__label_privacy",
												"Privacy Policy"
											)}
										</a>
									</span>
								</Exist>
								<Exist when={!eq(TEAM_PREFIX, TeamType.TICATS)}>
									<span>
										{i18n.t(
											"terms.request.sponsor_marketing_label_p1",
											"I wish to receive"
										)}{" "}
										<b>
											{i18n.t(
												"terms.request.sponsor_marketing_label_p2",
												"Sponsor Marketing"
											)}
										</b>{" "}
										{i18n.t(
											"terms.request.sponsor_marketing_label_p3",
											"emails"
										)}
									</span>
								</Exist>
							</FlexContainer>
						</Exist>
					</Stack>
				</FormControl>
				<SubmitButton
					variant={"contained"}
					type="submit"
					disabled={
						isDisabledSubmit || !requiredFields.every((field) => eq(field.value, "1"))
					}>
					{i18n.t("terms.request.submit", "Submit")}
				</SubmitButton>
			</form>
		</React.Fragment>
	);
});
